import React from 'react'
import AntdSkeleton from 'antd/es/skeleton'
import type { SkeletonProps } from 'antd/es/skeleton'
import type { AvatarProps as SkeletonAvatarProps } from 'antd/es/skeleton/Avatar'
import type { SkeletonButtonProps } from 'antd/es/skeleton/Button'
import type { SkeletonImageProps } from 'antd/es/skeleton/Image'
import type { SkeletonInputProps } from 'antd/es/skeleton/Input'
import type { SkeletonNodeProps } from 'antd/es/skeleton/Node'

import { useDesignMode } from 'src/design-mode/useDesignMode'

import { Skeleton as ShadcnSeamSkeleton } from './Skeleton.shadcn'

/**
 * Compat seam leaf for antd's `antd/es/skeleton` — one of the six
 * presentational components put behind the design toggle in the same PR
 * (Input, Card, Alert, Divider, Empty, Skeleton), after `./Button` (#970),
 * `./Typography` + `./Space` (#971) and `./Tag` (#972).
 *
 * A LEAF, not the `src/antd` barrel, on purpose (PRO-130): the barrel's
 * `export * from 'antd'` pulls antd/es/index.js — the whole component barrel —
 * into whatever chunk imports it, and one call site for this module is
 * boot-static (`src/components/main/RouteChunkSkeleton.tsx`, the route-level
 * suspense fallback). `update-design` ships a real seam at this same path with
 * the same shape — a NAMED `Skeleton` and no default — so a redirected call
 * site needs no further change when that lands.
 *
 * ---------------------------------------------------------------------------
 * THE SWITCH, and its safety property
 *
 * `useDesignMode()` returns `'antd'` for everyone who has not picked "New
 * design" under Theme — which is the shipped default, so it is every page load
 * of every customer who has not opted in. In the `'antd'` branch each dispatcher below renders
 * antd's own component with the props it was given: same element, same class
 * names, same DOM. The only difference from
 * `export { default as Skeleton } from 'antd/es/skeleton'` — which is what
 * this file was — is one extra component in the React tree, which renders
 * nothing of its own. `./__tests__/skeleton-seam-parity.test.tsx` asserts that
 * byte for byte across a prop matrix.
 *
 * antd's five statics are carried across explicitly, NOT by copying antd's
 * component object: `Object.assign(dispatcher, AntdSkeleton)` would also copy
 * whatever internals antd hangs off it. Each dispatches on the mode exactly as
 * the root does, so the seam cannot end up half-switched — `Button` (17 call
 * sites), `Input` (7), `Node` (5) and `Avatar` (3) are live; `Image` has none
 * today and is dispatched anyway, exactly as `Button.Group` is.
 * ------------------------------------------------------------------------- */
const SkeletonDispatcher = (props: SkeletonProps) => {
  const designMode = useDesignMode()

  if (designMode === 'shadcn') return <ShadcnSeamSkeleton {...props} />

  return <AntdSkeleton {...props} />
}

SkeletonDispatcher.displayName = 'Skeleton'

const SkeletonButtonDispatcher = (props: SkeletonButtonProps) => {
  const designMode = useDesignMode()

  if (designMode === 'shadcn') return <ShadcnSeamSkeleton.Button {...props} />

  return <AntdSkeleton.Button {...props} />
}

SkeletonButtonDispatcher.displayName = 'Skeleton.Button'

const SkeletonInputDispatcher = (props: SkeletonInputProps) => {
  const designMode = useDesignMode()

  if (designMode === 'shadcn') return <ShadcnSeamSkeleton.Input {...props} />

  return <AntdSkeleton.Input {...props} />
}

SkeletonInputDispatcher.displayName = 'Skeleton.Input'

const SkeletonAvatarDispatcher = (props: SkeletonAvatarProps) => {
  const designMode = useDesignMode()

  if (designMode === 'shadcn') return <ShadcnSeamSkeleton.Avatar {...props} />

  return <AntdSkeleton.Avatar {...props} />
}

SkeletonAvatarDispatcher.displayName = 'Skeleton.Avatar'

const SkeletonNodeDispatcher = (props: SkeletonNodeProps) => {
  const designMode = useDesignMode()

  if (designMode === 'shadcn') return <ShadcnSeamSkeleton.Node {...props} />

  return <AntdSkeleton.Node {...props} />
}

SkeletonNodeDispatcher.displayName = 'Skeleton.Node'

const SkeletonImageDispatcher = (props: SkeletonImageProps) => {
  const designMode = useDesignMode()

  if (designMode === 'shadcn') return <ShadcnSeamSkeleton.Image {...props} />

  return <AntdSkeleton.Image {...props} />
}

SkeletonImageDispatcher.displayName = 'Skeleton.Image'

export const Skeleton = Object.assign(SkeletonDispatcher, {
  Button: SkeletonButtonDispatcher,
  Input: SkeletonInputDispatcher,
  Avatar: SkeletonAvatarDispatcher,
  Node: SkeletonNodeDispatcher,
  Image: SkeletonImageDispatcher,
})
